import * as B from '@blueprintjs/core';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';
import ReactDOM from 'react-dom';
import {Helmet} from 'react-helmet';

import LookoutIcon from 'app/components/Alerts/LookoutIcon';
import AnalyzePolygonPopupWithState from 'app/components/AnalyzePolygonPopup/AnalyzePolygonPopupWithState';
import AppNav from 'app/components/AppNav';
import {LegendUIProvider} from 'app/components/DeclarativeMap/LegendUIContext';
import ManageBasemapDialog from 'app/components/DeclarativeMap/ManageBasemapDialog';
import ManageLayersDialog from 'app/components/DeclarativeMap/ManageLayersDialog';
import {PixelInfoPopupProvider} from 'app/components/DeclarativeMap/PixelInspector/PixelInfoProvider';
import {QuickMapVectorLayer} from 'app/components/DeclarativeMap/QuickMapVectorLayers';
import ImageryTimeline from 'app/components/ImageryTimeline';
import {useImageryTimeline} from 'app/components/ImageryTimeline/useImageryTimeline';
import ReportExportWindow from 'app/components/ReportExport/ReportExportWindow';
import {ReportPortalProvider} from 'app/components/ReportExport/ReportPortalProvider';
import TabbedSidebar, {SidebarView} from 'app/components/TabbedSidebar/TabbedSidebar';
import {ToastNotification} from 'app/modules/Notification/types';
import {OverlayConfig} from 'app/modules/Remote/api';
import {ApiFeature, ApiFeatureData} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {
  ApiOrganization,
  ApiOrganizationUser,
  getMeasurementSystem,
} from 'app/modules/Remote/Organization';
import {ApiImageryContract, ApiProject} from 'app/modules/Remote/Project';
import {FeatureSelection} from 'app/pages/ProjectDashboardSwitch';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {FeaturesActions, GeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {PolygonDispatch, PolygonState} from 'app/providers/MapPolygonStateProvider';
import {
  InvalidateOrderableScenes,
  OrderableScenesLoader,
} from 'app/providers/OrderableScenesProvider';
import {ProjectsActions} from 'app/providers/ProjectsProvider';
import {ShareLinkProvider} from 'app/providers/ShareLinkProvider';
import {NotesActions, NotesState, StateApiNote} from 'app/stores/NotesStore';
import * as CONSTANTS from 'app/utils/constants';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import featureFlags from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import * as hookUtils from 'app/utils/hookUtils';
import {CachedApiGetterOptions, StatusMaybe} from 'app/utils/hookUtils';
import * as layerUtils from 'app/utils/layerUtils';
import * as mapUtils from 'app/utils/mapUtils';
import {Mode} from 'app/utils/mapUtils/modes';
import {
  getMultiFeaturePropertyParts,
  isMultiLocationProperty,
} from 'app/utils/multiFeaturePropertyUtils';
import * as noteUtils from 'app/utils/noteUtils';
import {paysAtCheckout} from 'app/utils/organizationUtils';

import AgentPanel from './components/AgentPanel';
import ChatPanel from './components/ChatPanel';
import DetailsPanel from './components/DetailsPanel';
import NotesPanel from './components/NotesPanel';
import OrderImageryModalContainer from './components/OrderImageryModalContainer';
import OrderImageryPanel from './components/OrderImageryPanel';
import ReportsPanel from './components/ReportsPanel';
import useAgentRunNotes from './hooks/useAgentRunNotes';
import useAnalyzeAreaTool from './hooks/useAnalyzeAreaTool';
import useChatMapControls from './hooks/useChatMapControls';
import useFewShotExemplars from './hooks/useFewShotExemplars';
import useMapLifecycle from './hooks/useMapLifecycle';
import useNotesFilterState from './hooks/useNotesFilterState';
import useNoteUrlState from './hooks/useNoteUrlState';
import useOrderImageryFlow from './hooks/useOrderImageryFlow';
import useReportExportPortal from './hooks/useReportExportPortal';
import useRunExemplars from './hooks/useRunExemplars';
import useTabbedSidebarState from './hooks/useTabbedSidebarState';
import Hotkeys from './Hotkeys';
import Map from './Map';
import {MAP_ZOOM_ANIMATION_DURATION_EASE} from './mapConstants';
import {MapStateDispatch} from './MapStateProvider';
import {MultiLocationPanel} from './MultiLocationPanel';
import Navigation from './Navigation';
import GridProvider from './PropertyGrid/GridProvider';
import QuickMapVectorDialog from './QuickMapVectorDialog';
import cs from './styles.module.scss';
import UrlSceneHandler from './UrlSceneHandler';
import {
  OVERLAY_POPUP_INSET,
  computeFocusGeometry,
  makeDefaultOverlayVisibility,
  makeOverlaySettings,
  promptForNoteCoordinates,
} from './utils';

const MAP_ZOOM_ANIMATION_DURATION_NONE = 0;

const NOTES_SIDEBAR_TITLE = 'Notes';
const LOOKOUT_SIDEBAR_TITLE = 'Lookout';
const DETAILS_SIDEBAR_TITLE = 'Details';
const AGENT_SIDEBAR_TITLE = 'Agent';
const ANALYZE_SIDEBAR_TITLE = 'Analysis';
const REPORTS_SIDEBAR_TILE = 'Reports';
const CHAT_SIDEBAR_TITLE = 'Chat';

export interface Props {
  projectsActions: ProjectsActions;
  pushNotification: (p: ToastNotification) => unknown;

  firebaseToken: string;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  organization: I.ImmutableOf<ApiOrganization>;
  organizationUsers: ApiOrganizationUser[];
  loggedInUserActions: LoggedInUserActions;
  changeSelection: (selection: FeatureSelection) => void;

  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  selectedFeatureIdParams: I.Set<string>;

  getFeatureData: hookUtils.CachedApiGetter<[number], I.ImmutableOf<ApiFeatureData[]>>;
  featureData: I.ImmutableListOf<ApiFeatureData> | null;

  imageRefs: mapUtils.MapImageRefs;
  imageRefFromUrl: mapUtils.MapImageRef | null;
  subsetFeatureBboxFromUrl: string | undefined;
  mode: Mode;
  mapStateDispatch: MapStateDispatch;
  showFeatureIdInTitle: boolean;

  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  geoJsonFeaturesLoader: GeoJsonFeaturesLoader;

  orderableScenesLoader: OrderableScenesLoader;
  invalidateOrderableScenes: InvalidateOrderableScenes;

  notesState: NotesState;
  notesActions: NotesActions;
  polygonState: PolygonState;
  polygonDispatch: PolygonDispatch;

  getCurrentImageryContract: (
    projectId: string,
    opts?: CachedApiGetterOptions
  ) => StatusMaybe<ApiImageryContract | null>;

  features: I.ImmutableOf<ApiFeature[]>;
  selectedFeatures: I.Set<I.ImmutableOf<ApiFeature>>;
  selectedFeature: I.ImmutableOf<ApiFeature> | null;
  featuresActions: FeaturesActions;
}

// These are tools that involve interacting with the map directly, whether it's drawing on the map or clicking on the map.
export type MapInteractionTool =
  | 'analyzePolygon'
  | 'measureDistance'
  | 'measureArea'
  | 'pixelInspector'
  | 'imageryTimeline';

// These are tools that do not involve interacting with the map directly.
// These are all rendered in the MapSettingsControl.
export type MapSettingsTool = 'manageLayers' | 'manageBasemap' | 'quickMapVector';

export default function MonitorProjectView({
  projectsActions,
  pushNotification,
  firebaseToken,
  profile,
  organization,
  organizationUsers,
  loggedInUserActions,
  changeSelection,
  selectedProject,
  selectedFeatureCollection,
  selectedFeatureIdParams,
  getFeatureData: _getFeatureData,
  featureData,
  imageRefs,
  imageRefFromUrl,
  subsetFeatureBboxFromUrl,
  mode,
  mapStateDispatch,
  showFeatureIdInTitle,
  overlayFeatureCollections,
  geoJsonFeaturesLoader,
  orderableScenesLoader,
  notesState,
  notesActions,
  polygonState,
  polygonDispatch,
  getCurrentImageryContract,
  features,
  selectedFeatures,
  selectedFeature,
  featuresActions,
}: Props) {
  // The single selected feature's geometry (or null when 0 or multiple).
  // Lifted here so both <GridProvider> and <Map> can use the same value
  // without each recomputing or one reading the other's context.
  const currentFeatureGeometry: ApiFeature['geometry'] = React.useMemo(() => {
    if (selectedFeatures.size !== 1) return null;
    return selectedFeatures.first().toJS().geometry;
  }, [selectedFeatures]);

  // State
  const [focusedGeometry, setFocusedGeometry] = React.useState<geojson.GeoJSON | null>(() =>
    computeFocusGeometry(selectedFeatures, selectedFeatureCollection)
  );
  const [animationDuration, setAnimationDuration] = React.useState<number>(
    MAP_ZOOM_ANIMATION_DURATION_NONE
  );
  // 0 features right now is for the tasking tool, where we don't want to be
  // distracted by a basemap image.
  const [isOverlayVisibleByName, setIsOverlayVisibleByName] = React.useState<
    Record<string, boolean>
  >(() => makeDefaultOverlayVisibility(selectedFeatureCollection, overlayFeatureCollections));
  const [showCostDebugger, setShowCostDebugger] = React.useState<boolean>(false);
  const [quickMapVectorLayers, setQuickMapVectorLayers] = React.useState<QuickMapVectorLayer[]>([]);
  const [panelHoveredFeatureId, setPanelHoveredFeatureId] = React.useState<number | null>(null);

  // ─── Feature hooks ────────────────────────────────────────────────────────

  const mapLifecycle = useMapLifecycle({organization, selectedFeatureIdParams});
  const {
    mapStyle,
    setMapStyle,
    mapPaddingOptions,
    onMapPaddingChanged,
    getLatestCameraOptions,
    mapboxMap,
    setMapboxMap,
  } = mapLifecycle;

  // Stable map-fit helpers. Defined here (above the hooks that consume them)
  // so the consumer hooks' dep arrays can list them honestly without an
  // eslint-disable.
  const focusOnFeatureGeometry = React.useCallback(() => {
    const geometry = computeFocusGeometry(selectedFeatures, selectedFeatureCollection);
    setFocusedGeometry(geometry);
    setAnimationDuration(MAP_ZOOM_ANIMATION_DURATION_NONE);
  }, [selectedFeatures, selectedFeatureCollection]);

  const zoomToGeometry = React.useCallback((geometry: geojson.GeoJSON | null) => {
    setFocusedGeometry(geometry === null ? null : {...geometry});
    setAnimationDuration(MAP_ZOOM_ANIMATION_DURATION_EASE);
  }, []);

  const sidebar = useTabbedSidebarState({
    notesState,
    selectedFeature,
  });
  const {
    tabbedSidebarState,
    setTabbedSidebarState,
    isLegendCollapsed,
    setIsLegendCollapsed,
    openDetailsSidebarAndScrollToLegend,
  } = sidebar;

  const analyzeAreaTool = useAnalyzeAreaTool({
    polygonDispatch,
    notesActions,
    tabbedSidebarState,
    setTabbedSidebarState,
  });
  const {
    activeMapInteractionTool,
    setActiveMapInteractionTool,
    activeMapSettingsTool,
    setActiveMapSettingsTool,
    isAnalyzeAreaPopupOpen,
    setIsAnalyzeAreaPopupOpen,
    resetAndCloseAA,
    openAnalyzePolygonPopup,
  } = analyzeAreaTool;

  const openAnalyzeArea = React.useCallback(() => {
    if (!isAnalyzeAreaPopupOpen) {
      setActiveMapInteractionTool('analyzePolygon');
      setIsAnalyzeAreaPopupOpen(true);
    }
  }, [isAnalyzeAreaPopupOpen, setActiveMapInteractionTool, setIsAnalyzeAreaPopupOpen]);

  const fewShotFeatureGeometry = React.useMemo(
    () => (selectedFeature ? (selectedFeature.get('geometry').toJS() as geojson.Geometry) : null),
    [selectedFeature]
  );
  // Only a single image has one resolution to check against; compare mode disables few-shot.
  const fewShotSourceId =
    imageRefs.length === 1 ? layerUtils.parseLayerKey(imageRefs[0].layerKey).sourceId : null;
  const fewShotSourceDetails = fewShotSourceId
    ? (CONSTANTS.SOURCE_DETAILS_BY_ID[
        fewShotSourceId as keyof typeof CONSTANTS.SOURCE_DETAILS_BY_ID
      ] as {resolution?: number} | undefined)
    : undefined;
  const fewShotSceneResolution = fewShotSourceDetails?.resolution ?? null;
  const fewShotFlow = useFewShotExemplars(
    fewShotFeatureGeometry,
    fewShotSceneResolution,
    zoomToGeometry
  );
  const {resetFewShot} = fewShotFlow;
  // Boxes belong to one property, and AgentPanel stays mounted across a property switch.
  const selectedFeatureId = selectedFeature?.get('id');
  React.useEffect(() => {
    resetFewShot();
  }, [selectedFeatureId, resetFewShot]);

  const agentRunNotesState = useAgentRunNotes({
    notesActions,
    zoomToGeometry,
    focusedNoteId: notesState.focusedNoteId,
  });
  const {
    agentRunNotes,
    handleAgentRunNotesChange,
    handleAgentNoteClick,
    handleShowAllObservations,
    handleClearFocus,
    clearAgentRunNotes,
  } = agentRunNotesState;

  const {
    visibleRunExemplars,
    handleRunExemplarsChange,
    runExemplarVisibility,
    setRunExemplarVisibility,
    clearRunExemplars,
  } = useRunExemplars();

  // AgentPanel runs this as an effect cleanup keyed on the callback identity, so it has to be
  // stable: an inline arrow would fire it on every view render and wipe the label mid-keystroke.
  const handleAgentPanelUnmount = React.useCallback(() => {
    clearAgentRunNotes();
    clearRunExemplars();
    resetFewShot();
  }, [clearAgentRunNotes, clearRunExemplars, resetFewShot]);

  const chatMapControls = useChatMapControls({
    organization,
    selectedProject,
    selectedFeature,
    selectedFeatures,
    features,
    imageRefs,
    mode,
    mapStateDispatch,
    mapboxMap,
    getLatestCameraOptions,
    changeSelection,
    tabbedSidebarState,
    setTabbedSidebarState,
    openAnalyzeArea,
    activeMapInteractionTool,
    isOverlayVisibleByName,
    drawnPolygon: polygonState.polygon,
    focusedNoteId: notesState.focusedNoteId,
    onNoteClick: handleAgentNoteClick,
  });

  const reportExportPortal = useReportExportPortal({setTabbedSidebarState});
  const {
    pendingNewReportExportState,
    activeReportExport,
    reportExportWindowRef,
    setReportExportModalState,
    openReportExportModal,
  } = reportExportPortal;

  const orderImageryFlow = useOrderImageryFlow({
    isSidebarExpanded: tabbedSidebarState.isExpanded,
  });
  const {
    isOrderImageryModalOpen,
    sceneToOrder,
    setSceneToOrder,
    orderImageryState,
    setOrderImageryState,
    openOrderImageryModal,
    closeOrderImageryModal,
    previewScenes,
    setPreviewScenes,
  } = orderImageryFlow;

  const notesFilterControls = useNotesFilterState({organization});
  const {notesFilterState, setNotesFilterState, extendDateRangeToInclude} = notesFilterControls;

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function setMergedIsOverlayVisibleByName(payload: Record<string, boolean>) {
    setIsOverlayVisibleByName((prev) => ({...prev, ...payload}));
  }

  // ─── Effects ──────────────────────────────────────────────────────────────

  // Zoom to selected features when the selection changes and all features are loaded.
  React.useEffect(() => {
    if (selectedFeatures.size === selectedFeatureIdParams.size) {
      focusOnFeatureGeometry();
    }
  }, [selectedFeatures, selectedFeatureIdParams.size, focusOnFeatureGeometry]);

  // Reset feature-scoped state when the selected feature(s) change.
  React.useEffect(() => {
    closeOrderImageryModal();
    setOrderImageryState((prev) => ({
      ...prev,
      orderSubset: false,
      subsetFeature: null,
      isAddingSubsetRectangle: false,
    }));
    setNotesFilterState();
    setActiveMapInteractionTool(null);
    setActiveMapSettingsTool(null);
    setIsOverlayVisibleByName(
      makeDefaultOverlayVisibility(selectedFeatureCollection, overlayFeatureCollections)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeatureIdParams]);

  const isImageryTimelineOpen = activeMapInteractionTool === 'imageryTimeline';
  const imageryTimeline = useImageryTimeline({
    isOpen: isImageryTimelineOpen,
    featureData,
    // After leaving compare mode the later scene is the one kept.
    layerKey: imageRefs[imageRefs.length - 1].layerKey,
    map: mapboxMap,
  });

  const closeImageryTimeline = React.useCallback(() => {
    const {displayed, layerKey} = imageryTimeline;
    if (displayed) {
      mapStateDispatch({
        type: 'SET_IMAGE_REFS',
        imageRefs: [{layerKey, cursor: displayed.frame.cursor}],
      });
    }
    setActiveMapInteractionTool(null);
  }, [imageryTimeline, mapStateDispatch, setActiveMapInteractionTool]);

  // The timeline plays a single scene sequence, so it leaves compare mode.
  React.useEffect(() => {
    if (isImageryTimelineOpen && mode === 'COMPARE') {
      mapStateDispatch({type: 'SET_MODE', mode: 'VIEW', imageRefIdxToKeep: 1});
    }
  }, [isImageryTimelineOpen, mode, mapStateDispatch]);

  // Clear the measure-distance tool when the user starts drawing a note geometry.
  React.useEffect(() => {
    if (notesState.addPendingNoteGeometryMode && activeMapInteractionTool === 'measureDistance') {
      setActiveMapInteractionTool(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesState.addPendingNoteGeometryMode, activeMapInteractionTool]);

  useNoteUrlState({
    notesState,
    notesActions,
    pushNotification,
    mapStateDispatch,
    extendDateRangeToInclude,
    setTabbedSidebarState,
    zoomToGeometry,
    focusOnFeatureGeometry,
  });

  // ─── Render ───────────────────────────────────────────────────────────────

  const hydratedFeatureCollection = React.useMemo(
    () => featureCollectionUtils.hydratedFeatureCollection(selectedFeatureCollection, features),
    [selectedFeatureCollection, features]
  );

  const overlaySettings = React.useMemo(
    () => makeOverlaySettings(selectedFeatureCollection, overlayFeatureCollections),
    [selectedFeatureCollection, overlayFeatureCollections]
  );

  const overlayBoundsByName = React.useMemo(() => {
    const byName: Record<string, geojson.Geometry | null> = {};
    overlayFeatureCollections.forEach((fc) => {
      byName[fc!.get('name')] = fc!.get('bounds')?.toJS() ?? null;
    });
    return byName;
  }, [overlayFeatureCollections]);

  const propertyBounds = React.useMemo(
    () =>
      selectedFeatures.size > 0
        ? (featureCollectionUtils.getBounds(hydratedFeatureCollection, selectedFeatures) ?? null)
        : null,
    [hydratedFeatureCollection, selectedFeatures]
  );

  const multiFeaturePropertyParts = React.useMemo(
    () => getMultiFeaturePropertyParts(features, selectedFeature),
    [features, selectedFeature]
  );
  const reportPropertyParts = React.useMemo<I.ImmutableOf<ApiFeature[]>>(
    () => I.List(multiFeaturePropertyParts.map((p) => p.feature)),
    [multiFeaturePropertyParts]
  );

  let activeNoteType: StateApiNote['noteType'] | null = null;
  if (tabbedSidebarState.isExpanded) {
    if (tabbedSidebarState.currentViewTitle === NOTES_SIDEBAR_TITLE) {
      activeNoteType = 'user';
    } else if (tabbedSidebarState.currentViewTitle === LOOKOUT_SIDEBAR_TITLE) {
      activeNoteType = 'alert';
    } else if (tabbedSidebarState.currentViewTitle === AGENT_SIDEBAR_TITLE) {
      activeNoteType = 'agent';
    }
  }

  // An observation card in the chat focuses its note without leaving the Chat
  // tab, which shows no notes of its own; draw that one note so the click has
  // something to land on.
  const chatFocusedNote =
    tabbedSidebarState.isExpanded &&
    tabbedSidebarState.currentViewTitle === CHAT_SIDEBAR_TITLE &&
    notesState.focusedNoteId !== null
      ? notesState.notes.find((note) => note.id === notesState.focusedNoteId)
      : undefined;
  if (chatFocusedNote) {
    activeNoteType = chatFocusedNote.noteType;
  }

  // True only when a run's observations are loaded, not merely on the agent tab; gates
  // showing every observation geom at once.
  const isViewingAgentObservations = activeNoteType === 'agent' && agentRunNotes.length > 0;

  function renderSidebarViews(): SidebarView[] {
    const visibleOverlayConfigs: OverlayConfig[] = overlayFeatureCollections
      .filter((fc) => isOverlayVisibleByName[fc!.get('name')])
      .map((fc) => {
        const setting = overlaySettings.find((s) => s.name === fc!.get('name'));
        return {
          feature_collection_id: fc!.get('id'),
          style: {
            color: setting?.color ?? '#000000',
            default_filled: setting?.defaultFilled ?? false,
            default_unfilled: setting?.defaultUnfilled ?? false,
          },
        };
      })
      .toArray();

    const imageryContract = getCurrentImageryContract(selectedProject.get('id'));
    const hasImageryContract = !!imageryContract.value;
    const paidImagery = selectedFeature ? featureUtils.getPaidImagery(selectedFeature) : null;

    // Only display the order imagery sidebar if it is an option to order imagery
    const showOrderImagery = hasImageryContract || !!paidImagery || paysAtCheckout(organization);

    const isProcessingData =
      selectedFeature?.getIn(['properties', 'status']) ===
      CONSTANTS.FEATURE_WAITING_FOR_PROCESSING_STATUS;

    const reportsViews: SidebarView[] = [
      {
        title: REPORTS_SIDEBAR_TILE,
        icon: <B.Icon icon="document" />,
        disabled: !selectedFeature,
        render: () => {
          if (!selectedFeature) {
            return null;
          }

          const isMultiLocation = isMultiLocationProperty(features, selectedFeature);

          return (
            <ReportsPanel
              projectId={selectedProject.get('id')}
              feature={selectedFeature}
              featureCollection={selectedFeatureCollection}
              propertyParts={reportPropertyParts}
              overlayFeatureCollections={overlayFeatureCollections}
              overlaySettings={overlaySettings}
              isMultiLocationProperty={isMultiLocation}
              organization={organization}
              notesState={notesState}
              notesFilterState={notesFilterState}
              pendingNewReportExportState={pendingNewReportExportState}
              setNotesFilterState={setNotesFilterState}
              setReportExportModalState={setReportExportModalState}
              openReportExportModal={openReportExportModal}
            />
          );
        },
      },
    ];

    const notesViews: SidebarView[] = [
      {
        title: NOTES_SIDEBAR_TITLE,
        icon: <B.Icon icon="annotation" />,
        disabled: !selectedFeature,
        render: () => {
          if (!selectedFeature) {
            return null;
          }

          return (
            <NotesPanel
              noteType="user"
              feature={selectedFeature}
              features={features}
              notesState={notesState}
              notesActions={notesActions}
              selectedFeatureCollection={selectedFeatureCollection}
              firebaseToken={firebaseToken}
              organization={organization}
              profile={profile}
              organizationUsers={organizationUsers}
              imageRefs={imageRefs}
              latestCameraOptions={getLatestCameraOptions()}
              isOverlayVisibleByName={isOverlayVisibleByName}
              setIsOverlayVisibleByName={setIsOverlayVisibleByName}
              zoomToGeometry={zoomToGeometry}
              mapStateDispatch={mapStateDispatch}
              openAnalyzePolygonPopup={openAnalyzePolygonPopup}
              notesFilterState={notesFilterState}
              setNotesFilterState={setNotesFilterState}
              setReportExportModalState={setReportExportModalState}
              updateFeature={featuresActions.updateFeature}
              setTags={loggedInUserActions.setOrganizationTags}
              promptForCoordinates={() => promptForNoteCoordinates(notesActions, zoomToGeometry)}
            />
          );
        },
      },
    ];

    const orderImageryViews: SidebarView[] = showOrderImagery
      ? [
          {
            title: 'Order',
            icon: <B.Icon icon="shopping-cart" />,
            disabled: !selectedFeature,
            render: () => {
              if (!selectedFeature) {
                return null;
              }

              return (
                <OrderImageryPanel
                  organization={organization}
                  profile={profile}
                  mode={mode}
                  imageRefs={imageRefs}
                  selectedFeatures={selectedFeatures}
                  selectedProject={selectedProject}
                  orderImageryState={orderImageryState}
                  setOrderImageryState={setOrderImageryState}
                  orderableScenesLoader={orderableScenesLoader}
                  getCurrentImageryContract={getCurrentImageryContract}
                  mapStateDispatch={mapStateDispatch}
                  featuresActions={featuresActions}
                  openOrderImageryModal={openOrderImageryModal}
                  setPreviewScenes={setPreviewScenes}
                />
              );
            },
          },
        ]
      : [];

    const alertsViews: SidebarView[] = [
      {
        title: LOOKOUT_SIDEBAR_TITLE,
        icon: <LookoutIcon />,
        render: () => {
          if (!selectedFeature) {
            return null;
          }

          return (
            <NotesPanel
              noteType="alert"
              feature={selectedFeature}
              features={features}
              notesState={notesState}
              notesActions={notesActions}
              selectedFeatureCollection={selectedFeatureCollection}
              firebaseToken={firebaseToken}
              organization={organization}
              profile={profile}
              organizationUsers={organizationUsers}
              imageRefs={imageRefs}
              latestCameraOptions={getLatestCameraOptions()}
              isOverlayVisibleByName={isOverlayVisibleByName}
              setIsOverlayVisibleByName={setIsOverlayVisibleByName}
              zoomToGeometry={zoomToGeometry}
              mapStateDispatch={mapStateDispatch}
              openAnalyzePolygonPopup={openAnalyzePolygonPopup}
              notesFilterState={notesFilterState}
              setNotesFilterState={setNotesFilterState}
              setReportExportModalState={setReportExportModalState}
              updateFeature={featuresActions.updateFeature}
              setTags={loggedInUserActions.setOrganizationTags}
              promptForCoordinates={() => promptForNoteCoordinates(notesActions, zoomToGeometry)}
            />
          );
        },
      },
    ];

    const analyzeAreaViews: SidebarView[] = [
      {
        title: ANALYZE_SIDEBAR_TITLE,
        icon: <B.Icon icon="timeline-line-chart" />,
        disabled: !selectedFeature || isProcessingData,
        disabledTooltip: isProcessingData
          ? 'Analysis will be available once your data is loaded'
          : undefined,
        hidePanelDrawer: true,
        onClick: () => {
          if (!isAnalyzeAreaPopupOpen) {
            setActiveMapInteractionTool('analyzePolygon');
            setIsAnalyzeAreaPopupOpen(true);
          }
        },
      },
    ];

    const agentViews: SidebarView[] = featureFlags.AGENT_SIDEBAR(organization, profile)
      ? [
          {
            title: AGENT_SIDEBAR_TITLE,
            icon: <B.Icon icon="clean" />,
            disabled: !selectedFeature,
            render: () => {
              if (!selectedFeature) {
                return null;
              }

              return (
                <AgentPanel
                  selectedFeature={selectedFeature}
                  imageRefs={imageRefs}
                  focusedNoteId={notesState.focusedNoteId}
                  mapStateDispatch={mapStateDispatch}
                  onActiveRunNotesChange={handleAgentRunNotesChange}
                  onActiveRunExemplarsChange={handleRunExemplarsChange}
                  runExemplarVisibility={runExemplarVisibility}
                  onRunExemplarVisibilityChange={setRunExemplarVisibility}
                  onNoteClick={handleAgentNoteClick}
                  onShowAllObservations={handleShowAllObservations}
                  onClearFocus={handleClearFocus}
                  onUnmount={handleAgentPanelUnmount}
                  fewShotFlow={fewShotFlow}
                />
              );
            },
          },
        ]
      : [];

    const chatViews: SidebarView[] = featureFlags.AGENT_CHAT(organization, profile)
      ? [
          {
            title: CHAT_SIDEBAR_TITLE,
            icon: <B.Icon icon="chat" />,
            render: () => <ChatPanel mapControls={chatMapControls} />,
          },
        ]
      : [];

    const firstLayerKey = imageRefs[0].layerKey;

    return [
      ...orderImageryViews,
      ...notesViews,
      ...reportsViews,
      ...analyzeAreaViews,
      ...alertsViews,
      ...agentViews,
      ...chatViews,
      {
        title: DETAILS_SIDEBAR_TITLE,
        icon: <B.Icon icon="info-sign" />,
        disabled: !selectedFeature,
        render: () => {
          if (!selectedFeature) {
            return null;
          }

          return (
            <DetailsPanel
              selectedFeature={selectedFeature}
              hydratedFC={hydratedFeatureCollection}
              overlayFeatureCollections={overlayFeatureCollections}
              visibleOverlayConfigs={visibleOverlayConfigs}
              organization={organization}
              organizationUsers={organizationUsers}
              profile={profile}
              selectedProject={selectedProject}
              firebaseToken={firebaseToken}
              imageRefs={imageRefs}
              activeLayerKey={firstLayerKey}
              batchPatchFeature={featuresActions.batchPatchFeature}
              batchArchiveFeature={featuresActions.batchArchiveFeatures}
              setTags={loggedInUserActions.setOrganizationTags}
            />
          );
        },
      },
    ];
  }

  return (
    <div id="projectView">
      <Helmet>
        <title>
          {selectedFeatures.size > 0
            ? selectedFeatures.first().getIn(['properties', 'name'])
            : selectedProject.get('name')}{' '}
          | Lens
        </title>
      </Helmet>
      <PixelInfoPopupProvider
        isOpen={activeMapInteractionTool === 'pixelInspector'}
        setIsOpen={(isOpen) => setActiveMapInteractionTool(isOpen ? 'pixelInspector' : null)}
        imageRefs={imageRefs}
        onAddNote={
          // Notes belong to a single feature. Offering this while another note
          // is open would replace it, losing its draft.
          selectedFeature && notesState.mayCreateNotes && !notesState.noteComposer
            ? ({text, coords}) =>
                notesActions.startComposingNote({
                  source: 'pixelInfo',
                  text,
                  geometry: {type: 'Point', coordinates: [coords.lng, coords.lat]},
                  anchor: {lng: coords.lng, lat: coords.lat},
                })
            : undefined
        }
      >
        <ShareLinkProvider
          cameraOptions={getLatestCameraOptions()}
          feature={selectedFeature}
          project={selectedProject}
          overlayFeatureCollections={overlayFeatureCollections}
          overlayVisibilityByName={isOverlayVisibleByName}
          overlaySettings={overlaySettings}
        >
          <ReportPortalProvider>
            <AppNav
              organization={organization}
              profile={profile}
              selectedProject={selectedProject}
              isMinimal={true}
              loggedInUserActions={loggedInUserActions}
              showHotkeys
            >
              <Navigation
                imageRefs={imageRefs}
                featureData={featureData}
                mode={mode}
                // TODO(emily): The `Navigation` component assumes the feature
                // collection is hydrated. Once we eliminate the hydrated feature
                // collection universally, we can pass in `features` instead of the
                // `hydratedFeatureCollection` object here.
                selectedFeatureCollection={hydratedFeatureCollection}
                selectedFeatureIdParams={selectedFeatureIdParams}
                selectedFeatures={selectedFeatures}
                organization={organization}
                profile={profile}
                selectedProject={selectedProject}
                mapStateDispatch={mapStateDispatch}
                changeSelection={changeSelection}
                showFeatureIdInTitle={showFeatureIdInTitle}
                isImageryTimelineOpen={isImageryTimelineOpen}
              />
            </AppNav>
            <div className={cs.container}>
              <TabbedSidebar
                views={renderSidebarViews()}
                hidden={!selectedFeature}
                state={tabbedSidebarState}
                setState={setTabbedSidebarState}
                setPaddingOptions={onMapPaddingChanged}
              />

              <LegendUIProvider
                isLegendCollapsed={isLegendCollapsed}
                setIsLegendCollapsed={setIsLegendCollapsed}
                onOpenDetailsLegendInSidebar={openDetailsSidebarAndScrollToLegend}
              >
                <GridProvider
                  projectId={selectedProject.get('id')}
                  featureId={
                    selectedFeatures.size === 1 ? selectedFeatures.first().get('id') : null
                  }
                  featureGeometry={currentFeatureGeometry}
                >
                  <Map
                    firebaseToken={firebaseToken}
                    profile={profile}
                    organization={organization}
                    currentFeatureGeometry={currentFeatureGeometry}
                    // TODO(emily): The `Map` component assumes the feature collection
                    // is hydrated. Once we eliminate the hydrated feature collection
                    // universally, we can pass in `features` instead of the
                    // `hydratedFeatureCollection` object here.
                    selectedFeatureCollection={hydratedFeatureCollection}
                    selectedFeatures={selectedFeatures}
                    setSelectedFeatureIds={(featureLensIds) =>
                      changeSelection({
                        organizationId: organization.get('id'),
                        projectId: selectedProject.get('id'),
                        featureIds: featureLensIds,
                      })
                    }
                    imageRefs={imageRefs}
                    featureData={featureData}
                    // Disables compare mode when more than one property is selected
                    mode={selectedFeatureIdParams.size === 1 ? mode : 'VIEW'}
                    activeMapInteractionTool={activeMapInteractionTool}
                    setActiveMapInteractionTool={setActiveMapInteractionTool}
                    activeMapSettingsTool={activeMapSettingsTool}
                    setActiveMapSettingsTool={setActiveMapSettingsTool}
                    focusedGeometry={focusedGeometry}
                    animationDuration={animationDuration}
                    zoomToGeometry={zoomToGeometry}
                    // We provide default camera options for continuity between view and
                    // compare modes. We save the camera options (center, pitch, bearing,
                    // zoom) instead of bounds because when the bearing or pitch is
                    // non-zero, the bounds will be the smallest bounds that encompass
                    // the visible region. This can create a situation where the map is
                    // fit to bounds from a pitched map, and then the pitch is re-applied
                    // to the map, causing the map to zoom out when switching modes.
                    defaultCameraOptions={getLatestCameraOptions()}
                    notesState={
                      isViewingAgentObservations
                        ? {...notesState, notes: agentRunNotes}
                        : notesState
                    }
                    notesActions={notesActions}
                    showNotes={!!activeNoteType || !!notesState.noteComposer}
                    onSelectImageRefs={(imageRefs) =>
                      mapStateDispatch({type: 'SET_IMAGE_REFS', imageRefs})
                    }
                    setTags={loggedInUserActions.setOrganizationTags}
                    showAllAgentObservations={isViewingAgentObservations}
                    onClickNoteMarker={(geometry, note) => {
                      // Selecting the polygon will zoom to it. This case handles
                      // zooming to notes.
                      zoomToGeometry(geometry);

                      if (note) {
                        notesActions.focusNote(note.id);
                      }
                    }}
                    polygonState={polygonState}
                    polygonDispatch={polygonDispatch}
                    overlayFeatureCollections={overlayFeatureCollections}
                    geoJsonFeaturesLoader={geoJsonFeaturesLoader}
                    isOverlayVisibleByName={isOverlayVisibleByName}
                    overlaySettings={overlaySettings}
                    mapPaddingOptions={mapPaddingOptions}
                    mapStyle={mapStyle}
                    filterNotesCallback={
                      chatFocusedNote
                        ? (n) => n.id === chatFocusedNote.id
                        : isViewingAgentObservations
                          ? // While reviewing a run, agentRunNotes is already scoped to
                            // that run, so keep every observation that hasn't been
                            // dismissed — both new (agent) and saved (user) notes —
                            // rather than restricting to a single noteType.
                            (n) => !n.isArchived
                          : (n) =>
                              !!activeNoteType &&
                              noteUtils.filterNotesCallback(n, activeNoteType, notesFilterState)
                    }
                    showCostDebugger={showCostDebugger}
                    setShowCostDebugger={setShowCostDebugger}
                    orderImageryState={orderImageryState}
                    setOrderImageryState={setOrderImageryState}
                    tabbedSidebarState={tabbedSidebarState}
                    fewShotFlow={fewShotFlow}
                    runExemplars={visibleRunExemplars}
                    measurementSystem={getMeasurementSystem(organization)}
                    openAnalyzePolygonPopup={openAnalyzePolygonPopup}
                    quickMapVectorLayers={quickMapVectorLayers}
                    onMapInit={setMapboxMap}
                    externalHoveredFeatureId={panelHoveredFeatureId}
                    onCloseImageryTimeline={closeImageryTimeline}
                    imageryTimelineDisplayed={imageryTimeline.displayed}
                  />
                </GridProvider>
                <div className={cs.overlayControlsWrapper}>
                  {activeMapSettingsTool === 'manageLayers' && (
                    <ManageLayersDialog
                      organization={organization}
                      profile={profile}
                      onClose={() => setActiveMapSettingsTool(null)}
                      overlaySettings={overlaySettings}
                      setOverlaySettings={(overlays) => {
                        projectsActions.updateFeatureCollection(
                          selectedFeatureCollection.get('id'),
                          featureCollectionUtils.updateLensView(selectedFeatureCollection, (v) =>
                            v.set('overlays', I.fromJS(overlays))
                          )
                        );
                      }}
                      isOverlayVisibleByName={isOverlayVisibleByName}
                      setIsOverlayVisibleByName={setMergedIsOverlayVisibleByName}
                      overlayFeatureCollections={overlayFeatureCollections}
                      overlayBoundsByName={overlayBoundsByName}
                      propertyBounds={propertyBounds}
                    />
                  )}
                  {activeMapSettingsTool === 'manageBasemap' && (
                    <ManageBasemapDialog
                      onClose={() => setActiveMapSettingsTool(null)}
                      onChangeMapStyle={(nextMapStyle) => setMapStyle(nextMapStyle)}
                      selectedStyle={mapStyle}
                    />
                  )}
                </div>
                {activeMapSettingsTool === 'quickMapVector' && (
                  <QuickMapVectorDialog
                    layers={quickMapVectorLayers}
                    setLayers={setQuickMapVectorLayers}
                    overlayUploadOptions={{
                      projectId: selectedProject.get('id'),
                      pushNotification,
                      onOverlayAdded: (name) => setMergedIsOverlayVisibleByName({[name]: true}),
                    }}
                  />
                )}
              </LegendUIProvider>

              <div
                style={{
                  position: 'absolute',
                  paddingTop: (mapPaddingOptions.top ?? 0) + OVERLAY_POPUP_INSET,
                  paddingRight: (mapPaddingOptions.right ?? 0) + OVERLAY_POPUP_INSET,
                  paddingBottom: (mapPaddingOptions.bottom ?? 0) + OVERLAY_POPUP_INSET,
                  paddingLeft: (mapPaddingOptions.left ?? 0) + OVERLAY_POPUP_INSET,
                }}
              >
                {selectedFeature && featureData ? (
                  <AnalyzePolygonPopupWithState
                    firebaseToken={firebaseToken}
                    featureCollection={selectedFeatureCollection}
                    feature={selectedFeature}
                    featureData={featureData}
                    imageRefs={imageRefs}
                    notesState={notesState}
                    notesActions={notesActions}
                    polygonState={polygonState}
                    polygonDispatch={polygonDispatch}
                    mapStateDispatch={mapStateDispatch}
                    organization={organization}
                    profile={profile}
                    isOpen={isAnalyzeAreaPopupOpen}
                    onClose={() => {
                      resetAndCloseAA();
                    }}
                    setTags={loggedInUserActions.setOrganizationTags}
                    isOverlayVisibleByName={isOverlayVisibleByName}
                  />
                ) : (
                  <></>
                )}
              </div>

              {isImageryTimelineOpen && selectedFeature && (
                <div
                  className={cs.imageryTimelineWrapper}
                  style={{
                    paddingLeft: (mapPaddingOptions.left ?? 0) + OVERLAY_POPUP_INSET,
                    paddingRight: (mapPaddingOptions.right ?? 0) + OVERLAY_POPUP_INSET,
                    paddingBottom: (mapPaddingOptions.bottom ?? 0) + OVERLAY_POPUP_INSET,
                  }}
                >
                  <ImageryTimeline timeline={imageryTimeline} onClose={closeImageryTimeline} />
                </div>
              )}

              <OrderImageryModalContainer
                selectedFeature={selectedFeature}
                selectedProject={selectedProject}
                organization={organization}
                profile={profile}
                isOrderImageryModalOpen={isOrderImageryModalOpen}
                sceneToOrder={sceneToOrder}
                setSceneToOrder={setSceneToOrder}
                orderImageryState={orderImageryState}
                previewScenes={previewScenes}
                orderableScenesLoader={orderableScenesLoader}
                getCurrentImageryContract={getCurrentImageryContract}
                closeOrderImageryModal={closeOrderImageryModal}
              />

              {selectedFeatures.size > 1 && (
                <MultiLocationPanel
                  features={features}
                  selectedFeature={selectedFeatures.first()!}
                  projectId={selectedProject.get('id')}
                  featureCollectionId={selectedFeatureCollection.get('id')}
                  onSelect={(lensId) =>
                    changeSelection({
                      organizationId: organization.get('id'),
                      projectId: selectedProject.get('id'),
                      featureIds: I.Set([lensId]),
                    })
                  }
                  hoverFeatureById={setPanelHoveredFeatureId}
                />
              )}

              {activeReportExport &&
                selectedFeature &&
                featureData &&
                ReactDOM.createPortal(
                  <ReportExportWindow
                    firebaseToken={firebaseToken}
                    organization={organization}
                    notesActions={notesActions}
                    projectId={selectedProject.get('id')}
                    geoJsonFeaturesLoader={geoJsonFeaturesLoader}
                    selectedFeatureCollection={selectedFeatureCollection}
                    selectedFeature={selectedFeature}
                    overlayFeatureCollections={overlayFeatureCollections}
                    overlaySettings={overlaySettings}
                    multiFeaturePropertyParts={multiFeaturePropertyParts}
                    notesFilterState={notesFilterState}
                    includeAllParts={activeReportExport.allParts}
                    savedReportId={activeReportExport.savedReportId}
                    reportTitle={activeReportExport.reportTitle}
                    refetchReports={activeReportExport.refetchReports}
                    reportWindow={reportExportWindowRef.current}
                  />,
                  activeReportExport.htmlEl
                )}
            </div>

            <Hotkeys
              organizationId={organization.get('id')}
              selectedFeatureCollection={selectedFeatureCollection}
              selectedFeatures={selectedFeatures}
              selectedProjectId={selectedProject.get('id')}
              features={features}
              imageRefs={imageRefs}
              featureData={featureData}
              mapStateDispatch={mapStateDispatch}
              notesState={notesState}
              notesActions={notesActions}
              blockedHotkeys={{
                down: isOrderImageryModalOpen,
                up: isOrderImageryModalOpen,
                // The imagery timeline steps its own scenes and leaves the map's scene alone.
                left: isImageryTimelineOpen,
                right: isImageryTimelineOpen,
                'shift + t': isImageryTimelineOpen,
                // The measure and analyze-area tools host their own note forms
                // and share the pending geometry, so a keystroke shouldn't open
                // a second form on top of one of theirs.
                n:
                  isOrderImageryModalOpen ||
                  isAnalyzeAreaPopupOpen ||
                  activeMapInteractionTool === 'measureArea' ||
                  activeMapInteractionTool === 'measureDistance',
              }}
            />
            <UrlSceneHandler
              imageRefFromUrl={imageRefFromUrl}
              subsetFeatureBboxFromUrl={subsetFeatureBboxFromUrl}
              mapStateDispatch={mapStateDispatch}
              orderableScenesLoader={orderableScenesLoader}
              selectedFeature={selectedFeature}
              openOrderImageryModal={openOrderImageryModal}
              setOrderImageryState={setOrderImageryState}
              orderImageryState={orderImageryState}
            />
          </ReportPortalProvider>
        </ShareLinkProvider>
      </PixelInfoPopupProvider>
    </div>
  );
}
